import { memo } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import {
  createThemeGradientFillCssVariables,
  createThemeKeyCss,
  namespaceCssScope,
  Theme,
  THEME_BASE,
} from '@tinderapp/ui-core';
import { UiEngineProvider, useTheme, useThemes } from '@tinderapp/ui-react';

import getSvgFillManagerInstance, { FILL_TYPE_LINEAR_GRADIENT } from 'Lib/SvgFillManager';
import useIsomorphicLayoutEffect from 'Hooks/useIsomorphicLayoutEffect';

import { getThemeIdOrLight } from 'Store/appSettings/selectors';
import { ThemeId } from 'Store/appSettings/types';

import { THEME_DARK, THEME_DEFAULT } from './themes';

const { registerSvgFills, unregisterSvgFill } = getSvgFillManagerInstance();

// Sandbox themes
const UI_ENGINE_THEMES = {
  light: THEME_DEFAULT,
  dark: THEME_DARK,
};

type ThemeGlobalStylesProps = {
  theme?: Theme;
  themes?: {
    [key: string]: Theme;
  };
  onlyFills?: boolean;
};

type ThemeVariablesProps = {
  namespace?: string;
  cssScope?: string;
  theme: Theme;
  baseTheme?: Theme;
  onlyFills?: boolean;
};

function removeBaseTokens(srcTheme: Theme, baseTheme?: Theme): Theme {
  if (!baseTheme) {
    return srcTheme;
  }
  const outTheme = { ...srcTheme };
  Object.entries(outTheme).forEach(([key, values]) => {
    if (typeof values === 'object') {
      outTheme[key] = { ...values };
      if (baseTheme[key]) {
        Object.entries(baseTheme[key]).forEach(([name, value]) => {
          if (value === srcTheme[key][name]) {
            delete outTheme[key][name];
          }
        });
      }
    }
  });
  return outTheme;
}

export const ThemeVariables = memo((props: ThemeVariablesProps) => {
  const {
    namespace,
    cssScope = namespaceCssScope(namespace),
    baseTheme,
    theme: srcTheme,
    onlyFills = false,
  } = props;

  // For SSR, useIsomorphicLayoutEffect does nothing, so register SVG fills during the render
  if (!__CLIENT__) {
    const { gradient } = srcTheme;
    // TODO: REMOVE-TYPE-ASSERTION - may need better typing for gradient object.
    const fills = (Object.entries(gradient as any) as [string, any][]).reduce(
      (acc, [patternKey, { angle, colors }]) => {
        const key = `${namespace || srcTheme.themeId}__gradient--${patternKey}`;
        acc[key] = {
          type: FILL_TYPE_LINEAR_GRADIENT,
          params: {
            angle,
            stops: colors,
          },
        };
        return acc;
      },
      {}
    );
    registerSvgFills(fills as any);
  }

  // Add the gradients to SVG Fill manager
  useIsomorphicLayoutEffect(() => {
    const { gradient } = srcTheme;
    // TODO: REMOVE-TYPE-ASSERTION - may need better typing for gradient object.
    const fills = (Object.entries(gradient as any) as [string, any][]).reduce(
      (acc, [patternKey, { angle, colors }]) => {
        const key = `${namespace || srcTheme.themeId}__gradient--${patternKey}`;
        acc[key] = {
          type: FILL_TYPE_LINEAR_GRADIENT,
          params: {
            angle,
            stops: colors,
          },
        };
        return acc;
      },
      {}
    );
    registerSvgFills(fills as any);
    return () => {
      unregisterSvgFill(Object.keys(fills as any));
    };
  }, [srcTheme, namespace]);

  const getPatternId = (type: string, patternKey: string) =>
    `svg-fill-${type}__${namespace || srcTheme.themeId}__gradient--${patternKey}`;

  const styles = onlyFills
    ? `${cssScope}{${createThemeGradientFillCssVariables(
        removeBaseTokens(srcTheme, baseTheme).gradient,
        getPatternId
      )}}`
    : createThemeKeyCss(removeBaseTokens(srcTheme, baseTheme), cssScope, { getPatternId });

  return (
    <Helmet>
      <style type="text/css">{styles}</style>
    </Helmet>
  );
});

export const ThemeGlobalStyles = (props: ThemeGlobalStylesProps) => {
  const currentTheme = useTheme();
  const allThemes = useThemes();
  const theme = props.theme || currentTheme;
  const themes = props.themes || allThemes;
  const onlyFills = props.onlyFills || false;
  let rootTheme: Theme | undefined = theme;

  return (
    <>
      {Object.entries(themes).map(([themeId, themeContent]: [string, any]) => {
        let cssScope = namespaceCssScope(themeId);
        let baseTheme: Theme | undefined = THEME_BASE;
        if (themeContent === theme) {
          rootTheme = undefined;
          // For base theme, include theme variables in the root CSS scope...
          cssScope = `:root, ${cssScope}`;
          // ...and do not remove base tokens from the output
          baseTheme = undefined;
        }
        return (
          <ThemeVariables
            key={themeId}
            namespace={themeId}
            cssScope={cssScope}
            theme={themeContent}
            baseTheme={baseTheme}
            onlyFills={onlyFills}
          />
        );
      })}
      {/* Include root theme if it was not already included above */}
      {rootTheme ? <ThemeVariables theme={rootTheme} /> : null}
    </>
  );
};

const SandboxUiEngineProvider = (props: React.ComponentProps<typeof UiEngineProvider>) => {
  const themePreferenceId =
    useSelector(getThemeIdOrLight) === ThemeId.Dark ? ThemeId.Dark : ThemeId.Light;
  const themeId: keyof typeof UI_ENGINE_THEMES = themePreferenceId;
  return (
    <UiEngineProvider themes={UI_ENGINE_THEMES} themeId={themeId} {...props}>
      {props.children}
    </UiEngineProvider>
  );
};

export { UI_ENGINE_THEMES, SandboxUiEngineProvider as UiEngineProvider };
